/**
 * Local outgoing-transaction history for ZcashWallet.
 *
 * The shielded scanner sees INCOMING notes (that's its whole job) but
 * not our outgoing intent — once a send/swap broadcasts, the txid +
 * amount + recipient kind only live on this device. So we record
 * each successful broadcast here for the History pane.
 *
 * localStorage-backed, capped at 200 entries. Sorted newest-first
 * when read. No remote backup — losing it just loses display state,
 * not funds.
 */

const KEY = "telecrypt.tx-history.v1";
const MAX_ENTRIES = 200;

export type TxKind = "send" | "swap";

/**
 * Lifecycle:
 *   "pending"   — broadcast accepted but not yet observed in a block
 *   "confirmed" — for sends: scanner saw the spend in a finalised block.
 *                 for swaps: deposit confirmed AND MAYA emitted the outbound.
 *   "failed"    — broadcast rejected, or swap refunded/failed upstream
 */
export type TxStatus = "pending" | "confirmed" | "failed";

export interface TxRecord {
  /** Locally-generated id; not the on-chain txid. */
  id: string;
  /** The on-chain Sapling txid (hex). */
  txid: string;
  kind: TxKind;
  /** Amount in zats (8 decimals). */
  amount_zats: number;
  /** Fee in zats. */
  fee_zats: number;
  /** Recipient as the user knew it: u1…, t1…, or @username. */
  recipient: string;
  /** Optional username, when known (Contacts → Send). */
  recipient_username: string | null;
  /** Swap-only: destination asset id, e.g. "ARB.USDC-…". */
  swap_to_asset: string | null;
  /** Swap-only: destination address on the to_asset's chain. */
  swap_to_address: string | null;
  /** Memo plaintext (if any) — visible in history for the sender. */
  memo: string | null;
  /** ms since epoch. */
  broadcasted_at: number;
  /** Current status — defaults to "pending" on insert. */
  status?: TxStatus;
  /** Last time we polled or otherwise refreshed this row's status. */
  status_updated_at?: number;
  /**
   * Swap-only: human-readable upstream-protocol stage (e.g. "deposit
   * confirming", "swapped", "outbound broadcast"). Cleared once
   * status === "confirmed".
   */
  swap_stage?: string | null;
  /**
   * Source chain. `"zec"` for shielded sends/swaps (the default — older
   * rows predate this field and are always ZEC-origin). For EVM swaps:
   * `"ETH"` | `"BASE"` | `"ARB"` | `"OP"` | `"POLYGON"`. Used by the
   * Wallet detail sheet to pick the right block explorer.
   */
  origin_chain?: "zec" | "ETH" | "BASE" | "ARB" | "OP" | "POLYGON";
  /** LeoKit source-asset id (e.g. "ZEC.ZEC", "ETH.USDC-0x…"). */
  from_asset?: string;
  /** Source amount in the source asset's native units (string, no scale). */
  from_amount?: string;
}

function load(): TxRecord[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as TxRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(list: TxRecord[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_ENTRIES)));
  } catch {
    // quota / privacy mode — silent
  }
}

export function recordTransaction(
  rec: Omit<TxRecord, "id" | "broadcasted_at"> &
    Partial<Pick<TxRecord, "broadcasted_at">>,
): TxRecord {
  // Spread `rec` first, then layer the defaults on top — otherwise a
  // caller passing `broadcasted_at: undefined` clobbers our Date.now()
  // default. Same trick for the id field.
  const full: TxRecord = {
    status: "pending",
    ...rec,
    id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `tx_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
    broadcasted_at: rec.broadcasted_at ?? Date.now(),
  };
  const list = load();
  // Dedup by txid — multiple recordTransaction calls for the same
  // broadcast should refresh the row, not append.
  const filtered = list.filter((r) => r.txid !== full.txid);
  filtered.unshift(full);
  save(filtered);
  return full;
}

/**
 * Partial update by txid. Returns true if a row was found and updated.
 * Used by status pollers — balance scanner for sends, swap-state poll
 * for swaps.
 */
export function updateTransactionStatus(
  txid: string,
  patch: Partial<Pick<TxRecord, "status" | "swap_stage">>,
): boolean {
  const list = load();
  const idx = list.findIndex((r) => r.txid === txid);
  if (idx < 0) return false;
  list[idx] = {
    ...list[idx]!,
    ...patch,
    status_updated_at: Date.now(),
  };
  save(list);
  return true;
}

/** Read all pending rows — used to decide which to poll. */
export function pendingTransactions(): TxRecord[] {
  return load().filter((r) => (r.status ?? "pending") === "pending");
}

export function listTransactions(): TxRecord[] {
  return load().sort((a, b) => b.broadcasted_at - a.broadcasted_at);
}

export function recentTransactions(n: number): TxRecord[] {
  return listTransactions().slice(0, n);
}

export function clearTransactionHistory(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(KEY);
}
